import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Phone, RefreshCw, Eye, Loader2, Clock, CheckCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { useToast } from '../ui/Toast';
import { apiClient } from '../../lib/api/client';
import { CallAnalysisReportModal } from './CallAnalysisReportModal';

interface GenerateCallAnalysisButtonProps {
  candidateId: string;
  requirementId: string;
  candidateName?: string;
  requirementTitle?: string;
  className?: string;
  variant?: 'enterprise' | 'outline' | 'default';
}

// Progress stages for the report generation
const PROGRESS_STAGES = [
  { label: 'Initializing...', progress: 10, duration: 3000 },
  { label: 'Analyzing transcription...', progress: 25, duration: 15000 },
  { label: 'Evaluating skills...', progress: 45, duration: 30000 },
  { label: 'Generating insights...', progress: 65, duration: 45000 },
  { label: 'Building report...', progress: 85, duration: 60000 },
  { label: 'Finalizing...', progress: 95, duration: 90000 },
];

export const GenerateCallAnalysisButton: React.FC<GenerateCallAnalysisButtonProps> = ({
  candidateId,
  requirementId,
  candidateName,
  requirementTitle,
  className = '',
  variant = 'outline'
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [existingReport, setExistingReport] = useState<any>(null);
  const [callCount, setCallCount] = useState<number>(0);
  const [showReportModal, setShowReportModal] = useState(false);
  const [generatedReport, setGeneratedReport] = useState<any>(null);
  
  // Progress tracking state
  const [isPolling, setIsPolling] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const progressRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);
  
  const { toast } = useToast();

  const fetchExistingReport = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Check if report exists
      const response = await apiClient.getCallAnalysis(candidateId, requirementId);
      if (response.exists && response.report) {
        // Store with consistent structure (report_data inside)
        setExistingReport({
          report_id: response.report_id,
          session_id: response.session_id,
          call_count: response.call_count,
          overall_score: response.overall_score,
          report_data: response.report
        });
      } else {
        setExistingReport(null);
      }
      
      // Get call count
      try {
        const countResponse = await apiClient.getCallCount(candidateId, requirementId);
        if (countResponse.success) {
          setCallCount(countResponse.call_count || 0);
        }
      } catch {
        setCallCount(0);
      }
    } catch (error) {
      console.error('Error checking existing report:', error);
      setExistingReport(null);
    } finally {
      setIsLoading(false);
    }
  }, [candidateId, requirementId]);

  useEffect(() => {
    fetchExistingReport();
  }, [fetchExistingReport]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, []);

  // Start progress animation
  const startProgressAnimation = () => {
    startTimeRef.current = Date.now();
    setProgress(0);
    setElapsedTime(0);
    setProgressLabel(PROGRESS_STAGES[0].label);
    
    progressRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      setElapsedTime(Math.floor(elapsed / 1000));
      
      // Find current stage based on elapsed time
      let currentStage = PROGRESS_STAGES[0];
      for (const stage of PROGRESS_STAGES) {
        if (elapsed >= stage.duration) {
          currentStage = stage;
        }
      }
      
      // Smooth progress within stage
      const stageIndex = PROGRESS_STAGES.indexOf(currentStage);
      const nextStage = PROGRESS_STAGES[stageIndex + 1];
      
      if (nextStage) {
        const stageProgress = (elapsed - currentStage.duration) / (nextStage.duration - currentStage.duration);
        const smoothProgress = currentStage.progress + (nextStage.progress - currentStage.progress) * Math.min(stageProgress, 1);
        setProgress(Math.min(smoothProgress, 95));
        setProgressLabel(nextStage.label);
      } else {
        setProgress(95);
        setProgressLabel('Almost done...');
      }
    }, 500);
  };

  // Stop progress and polling
  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (progressRef.current) {
      clearInterval(progressRef.current);
      progressRef.current = null;
    }
    setIsPolling(false);
    setProgress(0);
    setElapsedTime(0);
  };

  // Poll for report completion
  const startPolling = () => {
    setIsPolling(true);
    startProgressAnimation();
    
    pollingRef.current = setInterval(async () => {
      try {
        const response = await apiClient.getCallAnalysis(candidateId, requirementId);
        
        if (response.exists && response.status === 'completed' && response.report) {
          // Report is ready!
          stopPolling();
          setProgress(100);
          
          toast({
            title: '✅ Report Ready!',
            description: 'Your call analysis report has been generated.',
            variant: 'success',
            duration: 5000
          });
          
          setGeneratedReport(response.report);
          setExistingReport({
            report_id: response.report_id,
            session_id: response.session_id,
            call_count: response.call_count,
            overall_score: response.overall_score,
            report_data: response.report
          });
          setShowReportModal(true);
          setIsGenerating(false);
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 8000); // Poll every 8 seconds
    
    // Auto-stop polling after 5 minutes
    setTimeout(() => {
      if (pollingRef.current) {
        stopPolling();
        setIsGenerating(false);
        toast({
          title: 'Taking longer than expected',
          description: 'Please click "View Report" in a minute to check if ready.',
          variant: 'default',
          duration: 6000
        });
      }
    }, 300000);
  };

  const handleGenerate = async (forceRegenerate: boolean = false) => {
    try {
      setIsGenerating(true);
      
      const result = await apiClient.generateCallAnalysis(candidateId, requirementId, forceRegenerate);
      
      if (result.status === 'transcribing') {
        toast({
          title: 'Transcribing Calls',
          description: result.message || 'Please wait 2-3 minutes and try again.',
          variant: 'default',
          duration: 5000
        });
        setIsGenerating(false);
        return;
      }
      
      if (result.status === 'processing') {
        // Start polling and show progress bar
        toast({
          title: '🔄 Generating Report',
          description: 'AI is analyzing the call. This takes 2-4 minutes.',
          variant: 'default',
          duration: 4000
        });
        startPolling();
        return; // Keep isGenerating true, polling will set it false when done
      }
      
      if (result.status === 'completed' && result.report) {
        toast({
          title: 'Report Generated',
          description: `Call analysis report generated from ${result.call_count || 0} calls.`,
          variant: 'success',
          duration: 4000
        });
        
        setGeneratedReport(result.report);
        setExistingReport({
          report_id: result.report_id,
          session_id: result.session_id,
          call_count: result.call_count,
          report_data: result.report
        });
        setShowReportModal(true);
        setIsGenerating(false);
      }
      
    } catch (error: any) {
      console.error('Error generating call analysis:', error);
      toast({
        title: 'Generation Failed',
        description: error.response?.data?.detail || error.message || 'Failed to generate report.',
        variant: 'error',
        duration: 5000
      });
      stopPolling();
      setIsGenerating(false);
    }
  };

  const handleViewReport = () => {
    if (existingReport) {
      setGeneratedReport(existingReport.report_data);
      setShowReportModal(true);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <Button variant={variant} disabled className={className}>
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        Loading...
      </Button>
    );
  }

  // Always show the button - disabled if no calls
  const hasNoCalls = callCount === 0 && !existingReport;

  // Format elapsed time as mm:ss
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Progress Modal Component - rendered via portal
  const progressModal = isPolling ? ReactDOM.createPortal(
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999 }}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4"
        style={{ zIndex: 100000 }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-white animate-spin" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Generating Report</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">AI is analyzing the call recording</p>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{progressLabel}</span>
            <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400">{Math.round(progress)}%</span>
          </div>
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
        
        {/* Time elapsed */}
        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-6">
          <div className="flex items-center gap-1.5">
            <Clock className="w-4 h-4" />
            <span>Time elapsed: {formatTime(elapsedTime)}</span>
          </div>
          <span>Est. 2-4 min</span>
        </div>
        
        {/* Steps indicator */}
        <div className="space-y-2">
          {[
            { label: 'Processing audio', done: progress >= 25 },
            { label: 'Analyzing skills & experience', done: progress >= 50 },
            { label: 'Evaluating communication', done: progress >= 70 },
            { label: 'Generating insights', done: progress >= 90 },
          ].map((step, idx) => (
            <div key={idx} className={`flex items-center gap-2 text-sm ${step.done ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
              {step.done ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                <div className="w-4 h-4 rounded-full border-2 border-current" />
              )}
              <span>{step.label}</span>
            </div>
          ))}
        </div>
        
        {/* Cancel button */}
        <button
          onClick={() => {
            stopPolling();
            setIsGenerating(false);
            toast({
              title: 'Generation Cancelled',
              description: 'Report generation was cancelled. The process may continue in the background.',
              variant: 'default',
              duration: 4000
            });
          }}
          className="mt-6 w-full py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        >
          Cancel and close
        </button>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      {/* Progress Modal Portal */}
      {progressModal}

      {existingReport ? (
        <div className={`flex items-center gap-2 ${className}`}>
          <Button
            variant="outline"
            size="sm"
            onClick={handleViewReport}
          >
            <Eye className="w-4 h-4 mr-1.5" />
            View Call Analysis
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleGenerate(true)}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-1.5" />
            )}
            Regenerate
          </Button>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleGenerate(false)}
          disabled={isGenerating || hasNoCalls}
          className={`${className} ${hasNoCalls ? 'opacity-50 cursor-not-allowed' : ''}`}
          title={hasNoCalls ? 'No completed calls with recordings available' : undefined}
        >
          {isGenerating ? (
            <>
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Phone className="w-4 h-4 mr-1.5" />
              {hasNoCalls ? 'Call Analysis (No calls)' : `Call Analysis ${callCount > 0 ? `(${callCount})` : ''}`}
            </>
          )}
        </Button>
      )}

      {showReportModal && generatedReport && (
        <CallAnalysisReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          report={generatedReport}
          candidateId={candidateId}
          requirementId={requirementId}
          candidateName={candidateName}
          requirementTitle={requirementTitle}
        />
      )}
    </>
  );
};

